import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../Breadcrumb";
import CommonEdit from "./CommonEdit";
import PatternKeisouEdit from "./PatternKeisouEdit";
import PatternSystemEdit from "./PatternSystemEdit";
import PatternKeisokuEdit from "./PatternKeisokuEdit";
import { fetchMenuData } from "../../actions/sidemenu";
import axiosInstance from "../../helpers/refreshTokenMiddleware";
import BASE_URL from "../../constants/Api";
import "./DocumentEdit.css";
import "../DocumentRegister/DocumentRegister.css";
import Modal from "../Modal/Modal";

const sortByDisplayIdx = (list = []) =>
  [...list].sort((a, b) => (a?.display_idx || 0) - (b?.display_idx || 0));

const formatDate = (value) => {
  if (!value) return "";

  const [datePart] = value.split(" ");
  const delimiter = datePart.includes("/") ? "/" : "-";
  const [year, month, day] = datePart.split(delimiter);

  if (!year || !month || !day) {
    return value;
  }

  const paddedMonth = String(month).padStart(2, "0");
  const paddedDay = String(day).padStart(2, "0");
  return `${year}年${paddedMonth}月${paddedDay}日`;
};

const formatDateTime = (value) => {
  if (!value) return "";

  const [datePart, timePart] = value.split(" ");
  const dateFormatted = formatDate(datePart);

  if (!timePart) {
    return dateFormatted;
  }

  const [hour = "00", minute = "00", second = "00"] = timePart.split(":");
  return `${dateFormatted} ${hour}時${minute}分${second}秒`;
};

const findCategoryText = (categoryList, id) => {
  if (id === undefined || id === null || id === "") return "";
  const numericId = Number(id);
  const found = categoryList.find(
    (item) => Number(item.document_category) === numericId
  );
  return found?.document_category_text || "";
};

const findTypeText = (typeList, id) => {
  if (id === undefined || id === null || id === "") return "";
  const numericId = Number(id);
  const found = typeList.find(
    (item) => Number(item.document_type) === numericId
  );
  return found?.document_type_text || "";
};

const DocumentEditBase = ({ user, menuData }) => {
  const { docId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [viewData, setViewData] = useState(null);
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [deptId, setDeptId] = useState(null);
  const [folderId, setFolderId] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [departmentName, setDepartmentName] = useState("");

  const [patternMaster, setPatternMaster] = useState({
    document_category: [],
    document_type: [],
  });

  const [masterError, setMasterError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  // 確認モーダル（戻るボタン用）
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");

  // 確認モーダル（編集確定ボタン用）
  const [isConfirmSaveModalOpen, setIsConfirmSaveModalOpen] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    title: "",
    issue_date: "",
    assignee: "",
    document_category: "",
    document_type: "",
    product_no: "",
    product_model: "",
    destination: "",
    customer: "",
    variety_name: "",
    device_name: "",
    assembly_type: "",
    category_name: "",
    product_name: "",
    original_doc_number: "",
    purpose: "",
    remarks: "",
  });

  //initialFormData
  const [initialFormData, setInitialFormData] = useState(null);

  useEffect(() => {
    if (!menuData || menuData.length === 0) {
      dispatch(fetchMenuData());
    }
  }, [dispatch, menuData]);

  const userName =
    user?.user_name || user?.name || user?.username || user?.displayName || "";

  useEffect(() => {
    if (!deptId || !folderId) {
      setPatternMaster({
        document_category: [],
        document_type: [],
      });
      return;
    }

    const fetchPatternMaster = async () => {
      try {
        setMasterError("");
        const response = await axiosInstance.post(`${BASE_URL}/top/data-new`, {
          department_id: deptId,
          folder_id: folderId,
        });

        const payload = response.data?.respons || {};
        setPatternMaster({
          document_category: sortByDisplayIdx(payload.document_category || []),
          document_type: sortByDisplayIdx(payload.document_type || []),
        });
      } catch (error) {
        console.error("Failed to fetch pattern master", error);
        setMasterError("図面種別情報を取得できませんでした。");
        setPatternMaster({
          document_category: [],
          document_type: [],
        });
      }
    };

    fetchPatternMaster();
  }, [deptId, folderId]);

  useEffect(() => {
    if (!docId) {
      setViewData(null);
      setEditError("文書を特定できませんでした。");
      return;
    }

    const fetchDocument = async () => {
      try {
        setEditLoading(true);
        setEditError("");
        const response = await axiosInstance.post(
          `${BASE_URL}/top/data-edit-info`,
          {
            doc_id: docId,
          }
        );

        if (response?.data?.status !== 200) {
          throw new Error(
            response?.data?.respons?.message || "文書情報の取得に失敗しました。"
          );
        }

        const payload = response.data?.respons || {};
        setViewData(payload);

        if (payload.department_id) {
          setDeptId(payload.department_id);
        }
        if (payload.folder_id) {
          setFolderId(payload.folder_id);
        }
        if (payload.folder_name) {
          setFolderName(payload.folder_name);
        }
        if (payload.department_name) {
          setDepartmentName(payload.department_name);
        }

        const issueDate = payload.issue_datetime
          ? payload.issue_datetime.split(" ")[0]
          : "";

        const initialData = {
          title: payload.title || "",
          issue_date: issueDate,
          assignee: payload.assignee || "",
          document_category: payload.document_category || "",
          document_type: payload.document_type || "",
          product_no: payload.product_no || "",
          product_model: payload.product_model || "",
          destination: payload.destination || "",
          customer: payload.customer || "",
          variety_name: payload.variety_name || "",
          device_name: payload.device_name || "",
          assembly_type: payload.assembly_type || "",
          category_name: payload.category_name || "",
          product_name: payload.product_name || "",
          original_doc_number: payload.original_doc_number || "",
          purpose: payload.purpose || "",
          remarks: payload.remarks || "",
        };
        setFormData(initialData);
        setInitialFormData(initialData);
      } catch (error) {
        console.error("文書情報の取得に失敗しました。", error);
        setEditError(
          error?.message ||
            "文書情報の取得に失敗しました。もう一度お試しください。"
        );
      } finally {
        setEditLoading(false);
      }
    };

    fetchDocument();
  }, [docId]);

  // deptIdが変更されたら、menuDataから部署名を取得してstateに設定
  useEffect(() => {
    if (deptId && menuData && menuData.length > 0 && !departmentName) {
      const currentDept = menuData.find((d) => d.department_id === deptId);
      if (currentDept?.department_name) {
        setDepartmentName(currentDept.department_name);
      }
    }
  }, [deptId, menuData, departmentName]);

  const handleFormDataChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSave = useCallback(() => {
    // 編集確定前ダイアログ表示
    setIsConfirmSaveModalOpen(true);
  }, []);

  const handleConfirmSave = useCallback(async () => {
    if (!docId) return;

    setIsConfirmSaveModalOpen(false);

    try {
      setIsSaving(true);
      setEditError("");

      //フォルダー
      if (!folderId) {
        setModalMessage("フォルダーは必須です。");
        setIsModalOpen(true);
        setIsSaving(false);
        return;
      }

      //タイトル
      if (!formData.title || !formData.title.trim()) {
        setModalMessage("タイトルは必須です。");
        setIsModalOpen(true);
        setIsSaving(false);
        return;
      }

      //計測の場合、Validate設定
      if (deptId === "D003") {
        const documentCategoryValue = formData.document_category
          ? String(formData.document_category).trim()
          : "";
        if (!documentCategoryValue) {
          setModalMessage("図面種類（計測）は必須です。");
          setIsModalOpen(true);
          setIsSaving(false);
          return;
        }
        const categoryNameValue = formData.category_name
          ? String(formData.category_name).trim()
          : "";
        if (!categoryNameValue) {
          setModalMessage("図面名称は必須です。");
          setIsModalOpen(true);
          setIsSaving(false);
          return;
        }
        if (!formData.product_model || !formData.product_model.trim()) {
          setModalMessage("製品形式は必須です。");
          setIsModalOpen(true);
          setIsSaving(false);
          return;
        }
        if (!formData.issue_date || !formData.issue_date.trim()) {
          setModalMessage("日付は必須です。");
          setIsModalOpen(true);
          setIsSaving(false);
          return;
        }
        if (!formData.assignee || !formData.assignee.trim()) {
          setModalMessage("担当者は必須です。");
          setIsModalOpen(true);
          setIsSaving(false);
          return;
        }
      }

      //YYYY/MM/DD → YYYY-MM-DD フォマットコンバート
      const formattedIssueDate = formData.issue_date
        ? formData.issue_date.replace(/\//g, "-")
        : "";

      // document_category
      const documentCategoryValue = formData.document_category
        ? Number(formData.document_category)
        : null;
      // document_type
      const documentTypeValue = formData.document_type
        ? Number(formData.document_type)
        : null;

      const getOptionalFieldValue = (value) => {
        if (value === null || value === undefined) return null;
        const trimmed = String(value).trim();
        return trimmed === "" ? null : trimmed;
      };

      const payload = {
        doc_id: docId,
        title: formData.title || "",
        issue_datetime: formattedIssueDate || "",
        assignee: formData.assignee || "",
        document_category: documentCategoryValue,
        document_type: documentTypeValue,
        product_no: getOptionalFieldValue(formData.product_no),
        product_model: getOptionalFieldValue(formData.product_model),
        destination: getOptionalFieldValue(formData.destination),
        customer: getOptionalFieldValue(formData.customer),
        variety_name: getOptionalFieldValue(formData.variety_name),
        device_name: getOptionalFieldValue(formData.device_name),
        assembly_type: getOptionalFieldValue(formData.assembly_type),
        category_name: getOptionalFieldValue(formData.category_name),
        product_name: getOptionalFieldValue(formData.product_name),
        original_doc_number: getOptionalFieldValue(
          formData.original_doc_number
        ),
        purpose: getOptionalFieldValue(formData.purpose),
        remarks: getOptionalFieldValue(formData.remarks),
      };

      // デバッグ: 値を確認するためにペイロードをログ出力
      console.log("DocumentEdit payload:", payload);
      console.log("formData.product_no:", formData.product_no);
      console.log(
        "payload.product_no:",
        payload.product_no,
        "type:",
        typeof payload.product_no
      );

      const response = await axiosInstance.post(
        `${BASE_URL}/top/data-edit`,
        payload
      );

      if (response?.data?.status !== 200) {
        throw new Error(
          response?.data?.respons?.message || "文書の更新に失敗しました。"
        );
      }

      setModalMessage("編集内容が確定されました。");
      setIsModalOpen(true);
    } catch (error) {
      console.error("文書の更新に失敗しました。", error);
      setEditError(
        error?.message || "文書の更新に失敗しました。もう一度お試しください。"
      );
    } finally {
      setIsSaving(false);
    }
  }, [docId, formData, deptId, folderId]);

  const hasFormDataChanged = useCallback(() => {
    if (!initialFormData) return false;

    const fieldsToCompare = [
      "title",
      "issue_date",
      "assignee",
      "document_category",
      "document_type",
      "product_no",
      "product_model",
      "destination",
      "customer",
      "variety_name",
      "device_name",
      "assembly_type",
      "category_name",
      "product_name",
      "original_doc_number",
      "purpose",
      "remarks",
    ];

    return fieldsToCompare.some(
      (field) => formData[field] !== initialFormData[field]
    );
  }, [formData, initialFormData]);

  //戻る処理
  const handleGoBack = useCallback(() => {
    //変更なデータチェック
    if (hasFormDataChanged()) {
      setConfirmMessage(
        "編集内容が変更されています。\n編集ページを離れてもよろしいですか？"
      );
      setIsConfirmModalOpen(true);
    } else {
      navigate(`/viewId/${docId}/view`);
    }
  }, [navigate, docId, hasFormDataChanged]);
  // 確認モーダルで「OK」を選択した場合の処理
  const handleConfirmGoBack = useCallback(() => {
    setIsConfirmModalOpen(false);
    navigate(`/viewId/${docId}/view`);
  }, [navigate, docId]);

  const issueDateText = formatDate(formData.issue_date);
  const registerDateText = formatDateTime(viewData?.u_datetime);
  const registrant = viewData?.user_name || "";
  const viewCountText =
    viewData?.view_count !== undefined && viewData?.view_count !== null
      ? `${viewData.view_count}回`
      : "";

  const documentCategoryText = findCategoryText(
    patternMaster.document_category,
    formData.document_category
  );
  const documentTypeText = findTypeText(
    patternMaster.document_type,
    formData.document_type
  );

  const patternContent = useMemo(() => {
    if (!viewData) return null;

    const patternProps = {
      formData,
      onFormDataChange: handleFormDataChange,
      documentCategories: patternMaster.document_category,
      documentTypes: patternMaster.document_type,
      documentCategoryText,
      documentTypeText,
      issueDateText,
    };

    if (deptId === "D001") {
      return <PatternKeisouEdit {...patternProps} />;
    }

    if (deptId === "D002") {
      return <PatternSystemEdit {...patternProps} />;
    }

    if (deptId === "D003") {
      return <PatternKeisokuEdit {...patternProps} />;
    }

    return null;
  }, [
    viewData,
    deptId,
    formData,
    handleFormDataChange,
    patternMaster,
    documentCategoryText,
    documentTypeText,
    issueDateText,
  ]);

  return (
    <div className="contents">
      <Breadcrumb
        userName={userName}
        departmentName={departmentName}
        folderName={folderName}
        deptId={deptId}
        folderId={folderId}
      />

      <div className="document-edit-container">
        <h1 className="document-edit-title">文書編集</h1>

        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            // モーダルを閉じた後に戻る
            if (modalMessage === "編集内容が確定されました。") {
              navigate(-1);
            }
          }}
          message={modalMessage}
        />
        {/* 確認モーダル（戻るボタン用） */}
        <Modal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={handleConfirmGoBack}
          message={confirmMessage}
          showCancelButton={true}
        />

        {/* 確認モーダル（編集確定ボタン用） */}
        <Modal
          isOpen={isConfirmSaveModalOpen}
          onClose={() => setIsConfirmSaveModalOpen(false)}
          onConfirm={handleConfirmSave}
          message="編集を確定します。よろしいですか?"
          showCancelButton={true}
        />

        {editLoading && (
          <div className="document-edit__loading">読込中です…</div>
        )}

        {editError && !editLoading && (
          <div className="document-edit__error">{editError}</div>
        )}

        {masterError && !editLoading && (
          <div className="document-edit__error">{masterError}</div>
        )}

        {viewData && !editLoading && (
          <>
            <CommonEdit
              folderName={folderName}
              title={formData.title}
              autoNumber={
                viewData.auto_number
                  ? `${viewData.folder_edition || ""}-${viewData.auto_number}`
                  : ""
              }
              registerDate={registerDateText}
              registrant={registrant}
              viewCount={viewCountText}
              onFormDataChange={handleFormDataChange}
            />

            {patternContent && (
              <div className="pattern-section">{patternContent}</div>
            )}

            {/* ボタングループ */}
            <div className="button-group edit-button-group">
              <button
                type="button"
                className="btn edit-back-btn"
                onClick={handleGoBack}
              >
                戻る
              </button>
              <button
                type="button"
                className="btn edit-confirm-btn"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? "保存中..." : "編集確定"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

DocumentEditBase.propTypes = {
  user: PropTypes.shape({
    user_name: PropTypes.string,
    name: PropTypes.string,
    username: PropTypes.string,
    displayName: PropTypes.string,
  }),
  menuData: PropTypes.arrayOf(
    PropTypes.shape({
      department_id: PropTypes.string,
      folder: PropTypes.arrayOf(
        PropTypes.shape({
          folder_id: PropTypes.string,
          folder_name: PropTypes.string,
        })
      ),
    })
  ),
};

DocumentEditBase.defaultProps = {
  user: null,
  menuData: [],
};

export default DocumentEditBase;
